import {
  Box,
  Breadcrumbs,
  Button,
  CardMedia,
  Dialog,
  Divider,
  IconButton,
  Link,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Link as RouterLink, useHistory, useRouteMatch } from 'react-router-dom';
import { styled } from '@mui/system';
import { useEffect, useState } from 'react';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { APPLICATION_TYPE, USER_ROLES } from '../../utils/constant';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { SxProps } from '@mui/system';
import { RequiredFieldsMessage } from '../RequiredFieldsMessage';
import LiveRegion from '../LiveRegion';
import { useLoadingAnnouncement } from '../../hooks/useLoadingAnnouncement';

// Investor Details Layout
const MenuItemList = [
  'RM/Distributor details',
  'Contributor Details',
  'Contact Details',
  'Additional KYC Details',
  'FATCA',
  'Nominee Details',
  "Investor's Bank Details",
  'Investment & Payment Details',
  'Document Details',
];
const HeaderList = [
  'RM/Distributor details',
  'Contributor Details',
  'Contact Details',
  'Additional KYC Details',
  'FATCA',
  'Nominee Details',
  "Investor's Bank Details",
  'Investment & Payment Details',
  'Document Details',
];
export const MenuItemListLink = [
  'distributor-details',
  'contributor-details',
  'contact-details',
  'additional-KYC-details',
  'fatca',
  'nominee-details',
  'bank-details',
  'investment-payment-details',
  'document-details',
];

const NonIndividualMenuItemList = [
  'RM/Distributor details',
  'Contributor Details',
  'Contact Details',
  'Investment & Payment Details',
  "Investor's Bank Details",
  'Authorised Signatory',
  'Declaration of UBO',
  'FATCA',
  'Document Details',
  // 'Risk Profile',
];

const NonIndividualHeaderList = [
  'RM/Distributor details',
  'Contributor Details',
  'Contact Details',
  'Investment & Payment Details',
  "Investor's Bank Details",
  'Authorised Signatory',
  'Declaration of Ultimate Beneficial Ownership (UBO)',
  'FATCA',
  'Document Details',
  // 'Risk Profile',
];

export const NonIndividualMenuItemListLink = [
  'distributor-details',
  'contributor-details',
  'contact-details',
  'investment-payment-details',
  'bank-details',
  'authorised-signatory',
  'declaration-ubo',
  'fatca',
  'document-details',
  // 'risk-profile',
];

export const InvestorDetailsLayout = ({
  applicationId,
  applicationNumber = '',
  applicant1ReferenceId = '',
  children,
}: {
  applicationId: string;
  applicationNumber: string;
  applicant1ReferenceId?: string;
  children: JSX.Element;
}): JSX.Element => {
  const { application } = useSelector((store: RootStateType) => store.application);
  const { currentStep } = application || {};
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { path } = useRouteMatch();
  const history = useHistory();
  const [transitionClassName, setTransitionClassName] = useState('');
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const result = useRouteMatch<{ slug: string }>({
    path: `${path}/:slug/`,
  });
  const activeItem = result
    ? application?.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemListLink.indexOf(result.params.slug)
      : MenuItemListLink.indexOf(result.params.slug)
    : 0;
  const menuList =
    application &&
    (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualMenuItemList
      : MenuItemList);
  const pageTitle =
    application &&
    (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? [USER_ROLES.AMCAPPROVER].includes(role)
        ? 'Edit Application'
        : 'Non Individual Investor Onboarding'
      : [USER_ROLES.INVESTOR, USER_ROLES.POAAPPROVER, USER_ROLES.AMCAPPROVER].includes(role)
      ? 'Edit Application'
      : 'Investor Onboarding');
  const sectionTitle =
    application &&
    (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
      ? NonIndividualHeaderList[activeItem]
      : HeaderList[activeItem]);
  const stepCount = menuList ? menuList.length : 0;
  const pendingCount = Math.max(stepCount - activeItem - 1, 0);
  const backPath =
    role === USER_ROLES.INVESTOR
      ? `/investment-details/${applicant1ReferenceId}/application-details`
      : [USER_ROLES.AMCAPPROVER, USER_ROLES.POAAPPROVER].includes(role)
      ? `/application-details/${applicationId}`
      : '/applications';

  const goToStep = (index: number, isActive: boolean) => {
    if (isActive) return;
    const linkPath =
      application &&
      (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
        ? NonIndividualMenuItemListLink[index]
        : MenuItemListLink[index]);
    history.push(`${linkPath}`, {
      id: applicationId,
      applicant1ReferenceId,
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setTransitionClassName('slideInUp');
    const timeoutId = setTimeout(() => {
      setTransitionClassName('');
    }, 300);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [activeItem]);
  return (
    <>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', mb: 2 }}>
        <IconButton aria-label="Back to previous step" onClick={() => history.push(backPath)}>
          <ArrowBackIosRoundedIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>Back</Typography>
      </Box>
      <Box
        component="nav"
        aria-label="Onboarding Steps"
        sx={{ display: { xs: 'block', md: 'none' }, mx: -2, mb: 2 }}>
        <Button
          variant="text"
          fullWidth
          id="application-button"
          aria-controls="application-dropdown"
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          startIcon={<CardMedia component="img" src="/images/person.svg" alt="Icon" />}
          endIcon={<ExpandMoreIcon />}
          sx={{
            color: 'text.primary',
            bgcolor: '#FFFFFF',
            px: 2,
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderBottom: '1px solid #E5E7EB',
            borderRadius: 0,
            '.MuiButton-endIcon': {
              ml: 'auto',
            },
          }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#6B7280' }}>
              Page {activeItem + 1}/{stepCount || 0}
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>{sectionTitle}</Typography>
          </Box>
        </Button>
        <Menu
          id="application-dropdown"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'application-button',
          }}
          sx={{
            '.MuiPaper-root ': {
              width: '100%',
              bgcolor: '#FFFFFF',
              '.MuiList-root': {
                p: 0,
                '.MuiDivider-root': { m: 0 },
                '.MuiMenuItem-root': {
                  '.MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: 14,
                  },
                  [`:nth-of-type(${activeItem + 1})`]: {
                    '.MuiListItemText-primary': {
                      fontWeight: 600,
                      color: '#2057A6',
                    },
                  },
                },
              },
            },
          }}>
          {menuList &&
            menuList.map((item, index) => {
              const isItemDisabled = !currentStep ? index !== 0 : index > currentStep;
              const isActive = index === activeItem;
              const linkPath =
                application &&
                (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? NonIndividualMenuItemListLink[index]
                  : MenuItemListLink[index]);
              return (
                <React.Fragment key={item}>
                  <MenuItem
                    component="a"
                    href={isActive ? undefined : linkPath}
                    role="link"
                    aria-label={item}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      if (!isActive) {
                        setAnchorEl(null);
                        goToStep(index, isActive);
                      }
                    }}
                    aria-current={isActive ? 'step' : undefined}
                    aria-disabled={isItemDisabled}
                    sx={{ minHeight: '30px', textDecoration: 'none' }}
                    disabled={isItemDisabled}>
                    <ListItemText
                      sx={{
                        color: isItemDisabled
                          ? '#9CA3AF'
                          : isActive
                          ? '#2057A6'
                          : index < (Number(currentStep) || 0)
                          ? '#1F7B7E'
                          : '#111827',
                        opacity: isItemDisabled ? 0.6 : 1,
                      }}>
                      {item}
                    </ListItemText>
                    <ArrowForwardIcon sx={{ color: '#2057A6' }} />
                  </MenuItem>
                  <Divider />
                </React.Fragment>
              );
            })}
        </Menu>
      </Box>

      <Box sx={{ mb: { xs: 2, md: 3 }, display: { xs: 'none', md: 'block' } }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.25,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Inter, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Link component={RouterLink} to={backPath} underline="hover" color="inherit">
            Applications
          </Link>
          <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>
            Investor Onboarding
          </Typography>
        </Breadcrumbs>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: 22, sm: 28 },
              fontWeight: 700,
              color: '#092056',
              lineHeight: 1.2,
            }}>
            {pageTitle}
          </Typography>
          {!!applicationNumber && (
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                color: '#6B7280',
              }}>
              Application ID: {applicationNumber}
            </Typography>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: { xs: 0, md: 2.5 },
        }}>
        <Box
          component="nav"
          aria-label="Onboarding Steps"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: 260,
            flexShrink: 0,
            position: 'sticky',
            top: 88,
            bgcolor: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '12px',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            px: 2.5,
            py: 3,
          }}>
          {menuList &&
            menuList.map((item, index) => {
              const isItemDisabled = !currentStep ? index !== 0 : index > currentStep;
              const isActive = index === activeItem;
              const reachedStep = Number(currentStep) || 0;
              const isCompleted = !isActive && index < reachedStep;
              const isLast = index === menuList.length - 1;
              const linkPath =
                application &&
                (application.applicationType === APPLICATION_TYPE.NON_INDIVIDUAL
                  ? NonIndividualMenuItemListLink[index]
                  : MenuItemListLink[index]);
              return (
                <Box
                  key={item}
                  component="a"
                  href={isActive || isItemDisabled ? undefined : linkPath}
                  aria-label={item}
                  aria-current={isActive ? 'step' : undefined}
                  aria-disabled={isItemDisabled}
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    if (!isItemDisabled && !isActive) {
                      goToStep(index, isActive);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    position: 'relative',
                    pb: isLast ? 0 : 3.25,
                    textDecoration: 'none',
                    cursor: isItemDisabled || isActive ? 'default' : 'pointer',
                    opacity: isItemDisabled ? 0.55 : 1,
                    pointerEvents: isItemDisabled ? 'none' : 'auto',
                  }}>
                  {!isLast && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 15,
                        top: 32,
                        bottom: 0,
                        width: '2px',
                        bgcolor: index < reachedStep ? '#22C55E' : '#E5E7EB',
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 13,
                      fontWeight: 600,
                      zIndex: 1,
                      ...(isCompleted
                        ? { bgcolor: '#22C55E', color: '#FFFFFF' }
                        : isActive
                        ? { bgcolor: '#2057A6', color: '#FFFFFF' }
                        : {
                            bgcolor: '#FFFFFF',
                            color: '#9CA3AF',
                            border: '1.5px solid #D1D5DB',
                          }),
                    }}>
                    {isCompleted ? <CheckIcon sx={{ fontSize: 16 }} /> : index + 1}
                  </Box>
                  <Box sx={{ pt: 0.4, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 500,
                        lineHeight: 1.35,
                        color: isActive ? '#2057A6' : isCompleted ? '#1F7B7E' : '#6B7280',
                      }}>
                      {item}
                    </Typography>
                    {isActive && (
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: 12,
                          fontWeight: 500,
                          color: '#64748B',
                        }}>
                        {activeItem + 1}/{stepCount} · {pendingCount} pending
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: '#FFFFFF',
            border: { xs: 'none', md: '1px solid #E5E7EB' },
            borderRadius: '12px',
            boxShadow: { xs: 'none', md: '0px 4px 16px rgba(26, 43, 60, 0.04)' },
            overflow: 'hidden',
          }}>
          <Box sx={{ px: { xs: 1, md: 3.5 }, pt: { xs: 1, md: 3 }, pb: 1 }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: 18, md: 20 },
                fontWeight: 700,
                color: '#111827',
                mb: 2,
              }}>
              {sectionTitle}
            </Typography>
            <Divider sx={{ borderColor: '#E5E7EB' }} />
          </Box>
          <Box sx={{ px: { xs: 1, md: 3.5 }, py: { xs: 2, md: 3 } }} className={transitionClassName}>
            <RequiredFieldsMessage />
            {children}
          </Box>
        </Box>
      </Box>
    </>
  );
};
// Investor Details Layout End

export const SubHeading = styled('h3')(({ theme }) => ({
  backgroundColor: '#EAF2FB',
  color: '#111827',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  borderRadius: '6px',
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  letterSpacing: 0,
  marginTop: '20px',
  marginBottom: '8px',
  padding: '12px 20px',
  [theme.breakpoints.down('md')]: {
    padding: '10px 16px',
  },
}));

export const ProceedSaveLater = ({
  saveLater,
  saveAndProceed,
  loader,
  clickedButton,
  saveButtonText = 'Save & Proceed',
  saveAndProceedType = 'submit',
  showEndIcon: _showEndIcon = false,
  showSaveForLater = true,
  showSaveAndProceed = true,
  disabled,
}: {
  saveLater?: () => void;
  saveAndProceed?: () => void;
  loader?: boolean;
  clickedButton?: string;
  saveAndProceedType?: 'button' | 'submit' | 'reset' | undefined;
  saveButtonText?: string;
  showEndIcon?: boolean;
  showSaveForLater?: boolean;
  showSaveAndProceed?: boolean;
  disabled?: boolean;
}): JSX.Element => {
  const loadingMessage = useLoadingAnnouncement(!!loader);
  const buttonSx = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.5,
    textTransform: 'none' as const,
    borderRadius: '8px',
    px: 2.5,
    py: 1,
    minHeight: 40,
    boxShadow: 'none',
  };
  return (
    <Box
      sx={{
        width: '100%',
        mt: 5,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.5,
        justifyContent: 'flex-end',
      }}>
      <LiveRegion message={loadingMessage} />
      {showSaveForLater && (
        <LoadingButton
          loadingPosition="start"
          type="submit"
          onClick={saveLater}
          loading={clickedButton === 'save for later' ? loader : false}
          variant="outlined"
          sx={{
            ...buttonSx,
            color: '#111827',
            border: '1px solid #D1D5DB',
            bgcolor: '#FFFFFF',
            '&:hover': { borderColor: '#9CA3AF', bgcolor: '#F9FAFB', boxShadow: 'none' },
          }}
          disabled={disabled ? disabled : loader}>
          Save for Later
        </LoadingButton>
      )}
      {showSaveAndProceed ? (
        <LoadingButton
          loadingPosition="start"
          type={saveAndProceedType}
          loading={clickedButton === 'save and proceed' ? loader : false}
          variant="contained"
          onClick={saveAndProceed}
          sx={{
            ...buttonSx,
            bgcolor: '#2057A6',
            color: '#FFFFFF',
            '&:hover': { bgcolor: '#1A4A8C', boxShadow: 'none' },
          }}
          disabled={disabled ? disabled : loader}>
          {saveButtonText}
        </LoadingButton>
      ) : (
        <LoadingButton
          loadingPosition="start"
          type={saveAndProceedType}
          loading={clickedButton === 'save and proceed' ? loader : false}
          variant="contained"
          sx={{
            ...buttonSx,
            bgcolor: '#2057A6',
            color: '#FFFFFF',
          }}
          disabled>
          {saveButtonText}
        </LoadingButton>
      )}
    </Box>
  );
};

export const NotesWithText = ({
  displayContent,
  displayContent1 = '',
  noteTitle = 'Note',
  background = 'rgba(51, 127, 201, 0.04)',
  textColor = 'rgba(41, 49, 57, 0.7)',
  sx,
}: {
  displayContent: string;
  displayContent1?: string;
  noteTitle?: string;
  background?: string;
  textColor?: string;
  sx?: SxProps;
}): JSX.Element => {
  return (
    <Typography
      component="div"
      sx={{
        backgroundColor: background,
        display: 'flex',
        flexDirection: 'row',
        padding: '10px 20px',
        borderRadius: '5px',
        alignItems: 'flex-start',
        mt: 0.5,
        mb: 1,
        width: '100%',
        boxSizing: 'border-box',
        ...sx,
      }}>
      <Typography
        sx={{
          color: textColor,
          fontSize: '13px',
          fontWeight: 500,
          ml: 0,
          display: 'flex',
        }}>
        <Typography
          component="span"
          sx={{
            color: textColor,
            fontSize: '13px',
            fontWeight: 600,
          }}>
          {noteTitle}:{'  '}
        </Typography>
        <Typography
          sx={{
            color: textColor,
            fontSize: '13px',
            fontWeight: 500,
            ml: 1,
          }}>
          {displayContent}
          {displayContent1 && (
            <Typography
              sx={{
                color: textColor,
                fontSize: '13px',
                fontWeight: 500,
                mt: 0.5,
              }}>
              {displayContent1}
            </Typography>
          )}
        </Typography>
      </Typography>
    </Typography>
  );
};

export const Notes = ({
  displayContent,
  displayContent1 = '',
}: {
  displayContent: string;
  displayContent1?: string;
}): JSX.Element => {
  return (
    <Typography
      component="div"
      sx={{
        backgroundColor: '#F3F4F6',
        display: 'flex',
        flexDirection: 'row',
        padding: '10px 16px',
        borderRadius: '8px',
        alignItems: 'flex-start',
        mt: 0.5,
        mb: 2,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <InfoOutlinedIcon sx={{ color: '#6B7280', fontSize: 18, mt: '1px' }} />
      <Typography
        sx={{
          color: 'rgba(41, 49, 57, 0.7)',
          fontSize: '13px',
          fontWeight: 500,
          ml: 1,
        }}>
        <Typography
          sx={{
            color: 'rgba(41, 49, 57, 0.7)',
            fontSize: '13px',
            fontWeight: 500,
            ml: 1,
          }}>
          {displayContent}
        </Typography>
        {displayContent1 && (
          <Typography
            sx={{
              color: 'rgba(41, 49, 57, 0.7)',
              fontSize: '13px',
              fontWeight: 500,
              ml: 1,
              mt: 0.5,
            }}>
            {displayContent1}
          </Typography>
        )}
      </Typography>
    </Typography>
  );
};

export const TwoNotes = ({
  displayContent1,
  displayContent2,
  displayContent3,
}: {
  displayContent1: string;
  displayContent2: string;
  displayContent3?: string;
}): JSX.Element => {
  return (
    <Typography
      component="div"
      sx={{
        backgroundColor: 'rgba(51, 127, 201, 0.04)',
        display: 'flex',
        flexDirection: 'row',
        padding: '10px 20px',
        borderRadius: '5px',
        alignItems: 'flex-start',
        mt: 0.5,
        mb: 1,
        width: '100%',
        boxSizing: 'border-box',
      }}>
      <InfoOutlinedIcon color="info" fontSize="small" />
      <Typography
        sx={{
          color: 'rgba(41, 49, 57, 0.7)',
          fontSize: '13px',
          fontWeight: 500,
          ml: 1,
        }}>
        <Typography
          sx={{
            color: 'rgba(41, 49, 57, 0.7)',
            fontSize: '13px',
            fontWeight: 500,
            ml: 1,
          }}>
          {displayContent1}
        </Typography>
        <Typography
          sx={{
            color: 'rgba(41, 49, 57, 0.7)',
            fontSize: '13px',
            fontWeight: 500,
            ml: 1,
            mt: 0.5,
          }}>
          {displayContent2}
        </Typography>
        {displayContent3 && (
          <Typography
            sx={{
              color: 'rgba(41, 49, 57, 0.7)',
              fontSize: '13px',
              fontWeight: 500,
              ml: 1,
              mt: 0.5,
            }}>
            {displayContent3}
          </Typography>
        )}
      </Typography>
    </Typography>
  );
};

export const InfoDialog = ({
  onClose,
  open,
  content,
}: {
  onClose: any;
  open: any;
  content: string;
}): JSX.Element => {
  return (
    <Dialog
      onClose={onClose}
      open={open}
      sx={{
        '.MuiPaper-root ': {
          maxWidth: 400,
          p: { xs: 2, sm: '15px 20px 45px' },
          borderRadius: '10px',
          height: 'unset',
          overflowY: 'auto',
        },
      }}>
      <IconButton onClick={onClose} sx={{ alignSelf: 'flex-end' }}>
        <CloseIcon fontSize="medium" />
      </IconButton>
      <Box sx={{ mb: 2, textAlign: 'center', '.MuiSvgIcon-root': { fontSize: '3.1875rem' } }}>
        <InfoOutlinedIcon color="info" fontSize="large" />
      </Box>
      <Box
        sx={{
          width: { xs: '100%', sm: '80%' },
          mx: 'auto',
          '.MuiTypography-root': {
            fontSize: 20,
            fontWeight: 500,
            textAlign: 'center',
          },
        }}>
        <Typography sx={{ color: 'secondary.main', lineHeight: '35px' }}>{content}</Typography>
      </Box>
    </Dialog>
  );
};
